<template>
  <v-app ref="appcontainer" class="lightBg is-relative overflow-clip test-class">
    <div id="app-notifications" :class="{ 'is-mobile': false }">
      <app-notification
        position="bottom right"
        class="mb-2"
        group="bottom-notifications"
        :max="5"></app-notification>
      <app-notification
        position="top right"
        class="mt-2 mr-2"
        group="top-notifications"
        :max="5"></app-notification>
    </div>
    <navigation-drawer
      v-if="!$route.meta.hideSidebar && !shouldRenderMobile"
      :unread-msg-count="unreadMessageCount"
      @supportBtnClick="openSupportChat"
      :orgRequired="orgRequired"></navigation-drawer>

    <feature-flag name="enable-notifications-v1">
      <template #enabled>
        <top-nav
          v-if="hasUser && !$route.meta.hideSidebar && !shouldRenderMobile"
          :user="$me"
          :unread-support-message-count="unreadMessageCount"
          @open-support-chat="openSupportChat" />
      </template>
    </feature-flag>

    <header-mobile v-if="hasUser && shouldRenderMobile"></header-mobile>

    <v-main :class="kebabCase($route.name)">
      <v-alert type="error" v-if="showDisabledOrgAlert" prominent text>
        {{ t('app.messages.viewOnlyModeInactiveOrg') }}
      </v-alert>
      <feature-flag name="enable-maintenance-mode">
        <template #enabled>
          <maintenance-banner v-if="!shouldRenderMobile"></maintenance-banner>
        </template>
      </feature-flag>
      <v-container fluid :class="mainContainerClass" v-if="$appLoaded">
        <h1 v-if="showPageTitle" class="mb-6">
          {{ $pageTitle || getRouteTitle($route.meta.title) }}
        </h1>
        <transition name="fade" mode="out-in">
          <div :class="pageWrapperClass">
            <new-org-welcome v-if="orgRequired"></new-org-welcome>
            <not-ready-message v-if="showNotReadyMessage"></not-ready-message>
            <router-view
              v-if="showRouterView"
              :class="{ 'is-mobile-content': shouldRenderMobile }"></router-view>
          </div>
        </transition>
      </v-container>
    </v-main>
    <new-terms :is-confirmation-needed="isConfirmationNeeded"></new-terms>
    <event-triggered-lazy-dialog-host
      event-name="open-remove-carrier-contact-dialog"
      :component="EventTriggeredRemoveCarrierContactDialog" />
    <event-triggered-lazy-dialog-host
      event-name="open-create-carrier-company-dialog"
      :component="EventTriggeredCreateCarrierCompanyDialog" />
    <event-triggered-lazy-dialog-host
      event-name="open-create-carrier-contact-dialog"
      :component="EventTriggeredCreateCarrierContactDialog" />

    <event-triggered-lazy-dialog-host
      event-name="open-create-yard-task-drawer"
      :component="EventTriggeredCreateTaskDrawer" />
    <event-triggered-lazy-dialog-host
      event-name="open-edit-yard-task-drawer"
      :component="EventTriggeredEditTaskDrawer" />
    <event-triggered-lazy-dialog-host
      event-name="open-create-yard-move-task-drawer"
      :component="EventTriggeredCreateMoveTaskDrawer" />
    <event-triggered-lazy-dialog-host
      event-name="open-drivers-chat"
      :component="EventTriggeredDriversChatDrawer" />

    <feature-flag name="enable-notifications-v1">
      <template #enabled>
        <event-triggered-lazy-dialog-host
          event-name="open-arrivals-drawer"
          :component="EventTriggeredArrivalsDrawer" />
      </template>
    </feature-flag>

    <!-- Here, not on the yard page, because the arrival details open it from any page -->
    <event-triggered-lazy-dialog-host
      event-name="open-link-appointment-dialog"
      :component="EventTriggeredLinkAppointmentDialog" />

    <event-triggered-lazy-dialog-host
      event-name="open-limits-overview-drawer"
      :component="EventTriggeredLimitsOverviewDrawer" />

    <event-triggered-lazy-dialog-host
      event-name="open-edit-appointment-dialog"
      :component="EventTriggeredEditAppointmentDialog" />

    <event-triggered-lazy-dialog-host
      event-name="open-yard-view-asset-create-dialog"
      :component="EventTriggeredYardViewAssetCreateDialog" />

    <event-triggered-lazy-dialog-host
      event-name="open-edit-custom-form-dialog"
      :component="EventTriggeredEditCustomFormDialog" />

    <feature-flag name="enable-ready-for-pickup-notification">
      <template #enabled>
        <event-triggered-lazy-dialog-host
          event-name="open-ready-for-pickup-notification-dialog"
          :component="EventTriggeredReadyForPickupNotificationDialog" />
      </template>
    </feature-flag>
  </v-app>
</template>

<script>
import { DateTime } from 'luxon';
import { mapState } from 'vuex';
import config from '@/config/config';
import { FeatureFlag, useFlag } from '@loadsmart/vue-feature-flags';

import EventTriggeredLazyDialogHost from '@satellite/components/elements/dialogs/EventTriggeredLazyDialogHost.vue';
import monitorWebsocketConnectionMixin from '@satellite/components/mixins/monitorWebsocketConnectionMixin';
import { getRouteTitle, isMobile } from '@satellite/plugins/util';
import { kebabCase } from 'lodash';
import { useRefetchOnSocketReconnect, useSettings } from '@/composables/index.js';
import { useZendeskMessenger } from '@/composables/useZendeskMessenger';
import { useAnalytics } from '@satellite/composables/useAnalytics';
import { useI18n } from 'vue-i18n';
import { defineAsyncComponent, markRaw } from 'vue';
import { useStore } from 'vuex';
import { useLocale } from 'vuetify';
import { useLocaleHelpers } from '@satellite/composables/useLocaleHelpers.ts';
import { useAppAuthenticatedLocale } from '@satellite/composables/useAppAuthenticatedLocale.ts';
import routeHelpers from '@satellite/helpers/routes.js';
import { EntitySettings, isInternalUser, isLoggedInAsAnotherUser } from '@nova/core';
import { Events } from '@/analytics/events';

const EventTriggeredRemoveCarrierContactDialog = markRaw(
  defineAsyncComponent(
    () =>
      import('./modules/carrier_contacts/components/EventTriggeredRemoveCarrierContactDialog.vue')
  )
);
const EventTriggeredCreateCarrierCompanyDialog = markRaw(
  defineAsyncComponent(
    () => import('./modules/carriers/components/EventTriggeredCreateCarrierCompanyDialog.vue')
  )
);
const EventTriggeredCreateCarrierContactDialog = markRaw(
  defineAsyncComponent(
    () => import('./modules/carriers/components/EventTriggeredCreateCarrierContactDialog.vue')
  )
);
const EventTriggeredCreateMoveTaskDrawer = markRaw(
  defineAsyncComponent(
    () => import('./modules/yard/components/EventTriggeredCreateMoveTaskDrawer.vue')
  )
);
const EventTriggeredCreateTaskDrawer = markRaw(
  defineAsyncComponent(() => import('./modules/yard/components/EventTriggeredCreateTaskDrawer.vue'))
);
const EventTriggeredEditTaskDrawer = markRaw(
  defineAsyncComponent(() => import('./modules/yard/components/EventTriggeredEditTaskDrawer.vue'))
);

const EventTriggeredDriversChatDrawer = markRaw(
  defineAsyncComponent(
    () => import('./components/drivers_chat/EventTriggeredDriversChatDrawer.vue')
  )
);
const EventTriggeredArrivalsDrawer = markRaw(
  defineAsyncComponent(() => import('./components/arrivals/EventTriggeredArrivalsDrawer.vue'))
);
const EventTriggeredLinkAppointmentDialog = markRaw(
  defineAsyncComponent(
    () => import('./modules/yard/components/EventTriggeredLinkAppointmentDialog.vue')
  )
);
const EventTriggeredLimitsOverviewDrawer = markRaw(
  defineAsyncComponent(
    () => import('./modules/appointments/components/limits/EventTriggeredLimitsOverviewDrawer.vue')
  )
);
const EventTriggeredReadyForPickupNotificationDialog = markRaw(
  defineAsyncComponent(
    () => import('./modules/yard/components/EventTriggeredReadyForPickupNotificationDialog.vue')
  )
);
const EventTriggeredEditAppointmentDialog = markRaw(
  defineAsyncComponent(
    () =>
      import('./modules/appointments/components/details-v2/EventTriggeredEditAppointmentDialog.vue')
  )
);
const EventTriggeredYardViewAssetCreateDialog = markRaw(
  defineAsyncComponent(
    () => import('./modules/yard/components/EventTriggeredYardViewAssetCreateDialog.vue')
  )
);
const EventTriggeredEditCustomFormDialog = markRaw(
  defineAsyncComponent(
    () => import('./modules/custom_forms/components/EventTriggeredEditCustomFormDialog.vue')
  )
);

function injectBeamerScript() {
  const BEAMER_SCRIPT_ID = 'beamer_script';
  // Avoid injecting the script multiple times
  if (document.getElementById(BEAMER_SCRIPT_ID)) {
    return null;
  }

  const script = document.createElement('script');
  script.id = BEAMER_SCRIPT_ID;
  script.type = 'text/javascript';
  script.src = 'https://app.getbeamer.com/js/beamer-embed.js';
  script.defer = 'defer';
  document.head.appendChild(script);
  return script;
}

export default {
  name: 'App',
  mixins: [monitorWebsocketConnectionMixin],
  components: {
    FeatureFlag,
    EventTriggeredLazyDialogHost
  },
  data() {
    return {
      isMobile: false,
      deviceAgnosticRoutes: [
        'login',
        'register',
        'resetPassword',
        'verifyAndResetPassword',
        'notAuthorized'
      ]
    };
  },
  computed: {
    ...mapState('navigation', {
      pageTitle: state => state.pageTitle
    }),
    shouldRenderMobile() {
      return this.isMobile && !this.deviceAgnosticRoutes.includes(this.$route.name);
    },
    orgRequired() {
      let shouldCheckOrg = !this.$route.meta.bypassOrgCheck;
      let notInternalUser = this.hasUser && !isInternalUser(this.$me);
      let hasNoOrg = this.hasUser && !this.$me.orgId;

      return shouldCheckOrg && (!this.hasUser || (notInternalUser && hasNoOrg));
    },
    hasUser() {
      return this.$me?.id;
    },
    showDisabledOrgAlert() {
      return this.$org && Boolean(this.$me?.orgId) && this.$org.isActive === false;
    },
    isInternalUser() {
      return isInternalUser(this.$me) || sessionStorage.getItem('login_origin') === 'loginAs';
    },
    isConfirmationNeeded() {
      return (
        Boolean(this.$me) &&
        !this.isInternalUser &&
        (!this.$me.tcConfirmedAt ||
          DateTime.fromISO(this.$me.tcConfirmedAt) < config.terms_conditions_last_update)
      );
    },
    /**
     * Whether the current route is accessible on mobile.
     * Device-agnostic routes (login, register, etc.) always pass through.
     * Any other route must explicitly opt-in via `meta.allowMobile`.
     */
    isMobileAllowedRoute() {
      return (
        this.deviceAgnosticRoutes.includes(this.$route.name) ||
        this.$route.meta.allowMobile === true
      );
    },
    /** Show the "not ready on mobile" placeholder for restricted routes. */
    showNotReadyMessage() {
      return (
        this.isMobile &&
        !this.orgRequired &&
        Boolean(this.$route.name) &&
        !this.isMobileAllowedRoute
      );
    },
    /** Show the router-view when the user has an org and the route is reachable. */
    showRouterView() {
      return !this.orgRequired && (!this.isMobile || this.isMobileAllowedRoute);
    },
    showPageTitle() {
      return !this.orgRequired && !this.$route.meta.customHeader && !this.isMobile;
    },
    mainContainerClass() {
      return {
        'viewport-height': this.orgRequired,
        'px-6 py-4': !this.$route.meta.fullScreen && !this.shouldRenderMobile,
        'pa-0 full-height': this.$route.meta.fullScreen || this.shouldRenderMobile
      };
    },
    pageWrapperClass() {
      return {
        'page-wrapper': !this.shouldRenderMobile,
        'full-height': this.shouldRenderMobile
      };
    },
    isNotificationsFlagResolved() {
      // `error` counts as resolved: a client that cannot reach Unleash has decided.
      return Boolean(this.notificationsFlag?.ready || this.notificationsFlag?.error);
    }
  },
  async mounted() {
    this.isMobile = isMobile();
    if (this.isMobile) {
      document.body.classList.add('is-mobile');
    }
    this.$store.commit('Settings/setSettingsMetadata', EntitySettings);
    this.$nextTick(() => {
      this.$store.dispatch('Auth/setInitialAccessToken');
    });

    this.$eventHub.$on('heartbeat', payload =>
      this.$store.dispatch('App/verifyNeutronVersion', payload)
    );

    const localSettings = this.$loadObjectFromLocalStorage('local_settings');
    this.$store.commit('Settings/setLocalSettings', localSettings);
    const { addSubspaceLoggingEventListener } = useSettings();
    if (localSettings.logSubspace) {
      addSubspaceLoggingEventListener();
    }

    this.$nextTick(() => {
      if (!this.isMobile) {
        this._syncTvKioskBodyClass();
      }
    });
  },
  beforeUnmount() {
    document.body.classList.remove('helios-tv-kiosk');
  },
  methods: {
    getRouteTitle,
    kebabCase,
    initBeamer() {
      const user = this.$me;
      // Beamer reads its config only once, on script load, so the button choice must wait for the flag.
      if (
        !this.isNotificationsFlagResolved ||
        !user?.id ||
        this.shouldRenderMobile ||
        isInternalUser(user) ||
        isLoggedInAsAnotherUser(user) ||
        this.$route.meta?.suppressThirdPartyWidgets
      ) {
        return;
      }

      window.beamer_config = {
        product_id: import.meta.env.VITE_BEAMER_PRODUCT_ID,
        // The top nav opens Beamer itself, replacing the floating button.
        button: !this.notificationsFlag.enabled,
        top: 14,
        right: 20,
        user_id: user.id,
        user_firstname: user.firstName,
        user_lastname: user.lastName,
        user_email: user.email,
        user_role: user.role,
        user_org_id: user.orgId
      };
      injectBeamerScript();
    },
    _syncTvKioskBodyClass() {
      document.body.classList.toggle(
        'helios-tv-kiosk',
        Boolean(this.$route?.meta?.suppressThirdPartyWidgets)
      );
    }
  },
  watch: {
    $route(newVal, oldVal) {
      if (!this.isMobile) {
        this._syncTvKioskBodyClass();
      } else {
        this.$store.commit('App/setMobilePageTitle', this.$route.meta.title);
      }
      const shouldUpdateRoute =
        newVal.query.appointmentId &&
        newVal.query.appointmentId !== oldVal.query?.appointmentId &&
        newVal.query.appointmentId !== this.$route.query.appointmentId;

      if (shouldUpdateRoute) {
        this.handleAppointmentQueryString(this.$route.path, newVal.query.appointmentId);
      }
    },
    $me() {
      this.initBeamer();
    },
    isNotificationsFlagResolved() {
      this.initBeamer();
    },
    '$i18n.locale'() {
      // Set page title when locale changes so it doesn't get "stuck" in initially selected language
      routeHelpers.setPageTitle(this.$store, this.$route, config.application_name ?? 'Opendock');
    }
  },
  setup() {
    const { locale, t } = useI18n({ useScope: 'global' });
    const { current } = useLocale();
    const store = useStore();
    const {
      getBaseLanguageCodeFromLocale,
      getLocale: getGuestPreferredLocale,
      getReadableLanguageFromLocale
    } = useLocaleHelpers();
    const analytics = useAnalytics();
    const trackWebsocketConnectionError = () =>
      analytics.track(Events.SYSTEM.WEBSOCKET_CONNECTION_ERROR);

    locale.value = getGuestPreferredLocale();

    useRefetchOnSocketReconnect();

    const notificationsFlag = useFlag('enable-notifications-v1');

    const { unreadMessageCount, openSupportChat } = useZendeskMessenger({
      isEnabled: () => !isMobile()
    });

    useAppAuthenticatedLocale({
      authenticatedUser: () => store.state.Auth.me,
      locale,
      vuetifyLocale: current,
      getBaseLanguageCodeFromLocale,
      onLocaleChange: newLocale => {
        analytics.registerSuper({
          application_language: getReadableLanguageFromLocale(newLocale)
        });
      }
    });

    return {
      t,
      EventTriggeredRemoveCarrierContactDialog,
      EventTriggeredCreateCarrierCompanyDialog,
      EventTriggeredCreateCarrierContactDialog,
      EventTriggeredCreateMoveTaskDrawer,
      EventTriggeredCreateTaskDrawer,
      EventTriggeredEditTaskDrawer,
      EventTriggeredDriversChatDrawer,
      EventTriggeredArrivalsDrawer,
      EventTriggeredLinkAppointmentDialog,
      EventTriggeredLimitsOverviewDrawer,
      EventTriggeredReadyForPickupNotificationDialog,
      EventTriggeredEditAppointmentDialog,
      EventTriggeredYardViewAssetCreateDialog,
      EventTriggeredEditCustomFormDialog,
      trackWebsocketConnectionError,
      unreadMessageCount,
      openSupportChat,
      notificationsFlag
    };
  }
};
</script>

<style lang="scss">
.is-mobile-content {
  margin-top: 75px;
}
.is-mobile {
  .dialog-content {
    padding: 0 !important;
  }

  .mobile-nav-dialog-body {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}
</style>
